<template>
  <DefaultPanel
    class="attach-list-panel"
    :collapsed="collapsed"
    :is-collapsible="true"
    :loading="isLoadingAttachments"
  >
    <template #header>
      <span class="icon">
        <i :class="iconClasses" />
      </span>
      <span>
        <slot name="title">{{ title }}</slot>
      </span>
      <span
        v-if="attachedFiles !== null"
        class="tag tag-count is-rounded is-primary"
      >
        {{ attachedFiles.length }}
      </span>
    </template>
    <template #header-controls>
      <Popover v-if="sweepableFiles.length > 0" placement="bottom">
        <span
          v-tooltip="{
            content: `
            ${sweepableFiles.length}
            ${pluralize('image', sweepableFiles.length)}
            are not detected in text anymore and can be deleted`,
            html: true,
          }"
          class="has-exponent-tag"
        >
          <a
            class="button is-small is-outlined is-rounded is-danger has-text-weight-semibold"
          >
            <span class="icon is-small">
              <i class="fa fa-broom fa-sm" />
            </span>
          </a>
          <span class="tag exponent-tag is-danger is-rounded no-border">
            {{ sweepableFiles.length }}
          </span>
        </span>
        <template #popper>
          <div class="is-flex is-flex-direction-column">
            <div class="pb-2">
              {{ sweepableFiles.length }}
              {{ pluralize('image', sweepableFiles.length) }}
              will be permanently deleted
            </div>
            <button
              class="button is-danger is-small has-text-weight-bold"
              :class="{ 'is-loading': isDeleting }"
              :disabled="isDeleting"
              style="margin-left: auto"
              @click="deleteAttachments(sweepableFiles.map((f) => f.id))"
            >
              <span class="text">OK</span>
            </button>
          </div>
        </template>
      </Popover>
    </template>
    <template v-if="canEdit && allowUpload && filepondUploadConfig" #toolbar>
      <FilePond
        :ref="filepondRef"
        class="fp-dropzone"
        name="file"
        :allow-multiple="true"
        :allow-drop="true"
        :allow-paste="false"
        :server="filepondUploadConfig"
        :label-idle="`Drag & Drop or
          <span class='filepond--label-action'>Browse</span>
          files (max 500MB)
        `"
        :label-file-processing-error="(err) => err.body"
        @processfile="handleProcessFile"
      />
    </template>
    <article
      v-if="attachedFiles.length || deleteSuccesses.length"
      class="attach-list-wrapper"
    >
      <header class="notification-container">
        <div
          v-for="delName of deleteSuccesses"
          :key="`delete-${delName}`"
          class="notification is-fullwidth is-success is-light m-0 mb-1 is-size-7"
        >
          <button
            class="delete mx-2"
            @click="
              () => {
                const iDel = deleteSuccesses.indexOf(delName)
                iDel !== -1 && deleteSuccesses.splice(iDel, 1)
              }
            "
          ></button>
          <span class="icon">
            <i class="fa-solid fa-trash" />
          </span>
          <span>Successfully deleted {{ delName }}</span>
        </div>
      </header>
      <div :class="{ 'has-selected': selectedAttachmentsIndices.length }">
        <SelectableRows
          :rows="attachedFiles"
          style="display: contents"
          @selected-indices="selectedAttachmentsIndices = $event"
        >
          <template
            #default="{
              selectableRows: selectableAttachments,
              toggleSelectRow,
              toggleSelectAllRows,
              allRowsSelected,
            }"
          >
            <div
              v-show="attachedFiles.length"
              class="control-bar is-size-7 mb-0"
            >
              <div v-if="canEdit" class="control-left">
                <a @click="toggleSelectAllRows">
                  {{ allRowsSelected ? 'Deselect' : 'Select' }} all...
                </a>
                <span v-if="selectedFiles.length" class="tag is-primary">
                  {{ selectedFiles.length }} selected
                </span>
              </div>
              <div class="control-right">
                <template v-if="selectedFiles.length">
                  <a
                    v-if="isEditing && canEmbed && embeddableFiles.length"
                    v-tooltip="
                      `Embed ${embeddableFiles.length} ${pluralize(
                        'item',
                        embeddableFiles.length,
                      )}`
                    "
                    class="button is-small is-outlined is-primary has-text-weight-bold"
                    @click="handleEmbedImages(embeddableFiles)"
                  >
                    <span class="icon mr-1">
                      <i class="fa-solid fa-plus fa-sm"></i>
                      <i class="fa-solid fa-i-cursor"></i>
                    </span>
                    {{ embeddableFiles.length }}
                  </a>
                  <Popover
                    v-if="canEdit && selectedFiles.length >= 1"
                    placement="bottom"
                  >
                    <a
                      class="button is-small is-outlined is-danger has-text-weight-bold"
                    >
                      <span class="icon mr-1">
                        <i class="fa fa-trash" />
                      </span>
                      {{ selectedFiles.length }}
                    </a>
                    <template #popper>
                      <div class="is-flex is-flex-direction-column">
                        <div class="pb-2">
                          {{ selectedFiles.length }}
                          {{ pluralize('image', selectedFiles.length) }}
                          will be permanently deleted
                        </div>
                        <button
                          class="button is-danger is-small has-text-weight-bold"
                          :class="{ 'is-loading': isDeleting }"
                          :disabled="isDeleting"
                          style="margin-left: auto"
                          @click="
                            deleteAttachments(selectedFiles.map((f) => f.id))
                          "
                        >
                          <span class="text">OK</span>
                        </button>
                      </div>
                    </template>
                  </Popover>
                </template>
                <template v-else>
                  <div class="select is-small">
                    <select v-model="sortKey">
                      <option value="name">Name</option>
                      <option value="created">Created</option>
                      <option value="modified">Modified</option>
                    </select>
                  </div>
                  <ModeSwitch
                    v-model="sortOrder"
                    :options="[
                      { value: 'asc', label: 'Ascending', shortLabel: 'Asc' },
                      {
                        value: 'desc',
                        label: 'Descending',
                        shortLabel: 'Desc',
                      },
                    ]"
                    color="primary"
                    force-select
                  >
                    <template #trigger="{ currentShortLabel }">
                      <a class="icon fa-stack fa-1x is-small">
                        <i
                          class="fa-solid fa-stack-1x fa-sort-up"
                          :class="{ 'fa-is-disabled': sortOrder === 'desc' }"
                        />
                        <i
                          class="fa-solid fa-stack-1x fa-sort-down"
                          :class="{ 'fa-is-disabled': sortOrder === 'asc' }"
                        />
                      </a>
                      <span>{{ currentShortLabel }}</span>
                    </template>
                  </ModeSwitch>
                </template>
              </div>
            </div>

            <div class="attach-list-content">
              <AttachmentFile
                v-for="(
                  { item: attachedFile, selected }, index
                ) in selectableAttachments"
                :key="`file-${attachedFile.id}`"
                class="attached-file"
                :class="{ 'is-loading': isLoadingAttachments }"
                :attached-file="attachedFile"
                :can-embed="canEmbed"
                :can-delete="canEdit"
                :is-selectable="canEdit"
                :is-selected="selected"
                :is-embedding="isEmbedding(attachedFile.id)"
                :is-deleting="isDeletingFile(attachedFile.id)"
                @select="canEdit && toggleSelectRow(index, $event.shiftKey)"
                @delete="canEdit && deleteAttachments([attachedFile.id])"
                @download="
                  downloadAttachment(attachedFile.id, attachedFile.name)
                "
                @embed="isEditing && handleEmbedImages([attachedFile])"
              ></AttachmentFile>
            </div>
          </template>
        </SelectableRows>
      </div>
    </article>
    <ImageFab
      v-if="isMobile && canEdit && allowUpload"
      :is-loading="isAddingFiles"
      @selected-images="addToFilePond"
    ></ImageFab>
  </DefaultPanel>
</template>

<script>
import { ref, computed, nextTick, inject } from 'vue'
import pluralize from 'pluralize'
import moment from 'moment'

import AttachmentFile from '@/components/AttachmentFile.vue'
import DefaultPanel from '@/components/DefaultPanel.vue'
import ImageFab from '@/components/ImageFAB.vue'
import SelectableRows from '@/components/SelectableRows.vue'

import { useEditor } from '@/composables/useEditor'
import { FilePond } from '@/libs/filepond'
import { useFilePond } from '@/composables/useFilePond'
import { useAttachments } from '@/composables/useAttachments'
import { scrollToElement } from '@/utils'

const strSort =
  (strKey, order = 'asc') =>
  (a, b) => {
    let sortRes = 0
    if (a[strKey] || b[strKey]) {
      sortRes = a[strKey].localeCompare(b[strKey], undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    }
    return order === 'desc' ? -sortRes : sortRes
  }
const dateSort =
  (dateKey, order = 'asc') =>
  (a, b) => {
    let sortRes = 0
    if (a[dateKey] && b[dateKey]) {
      sortRes = moment(a[dateKey]).valueOf() - moment(b[dateKey]).valueOf()
    }
    return order === 'desc' ? -sortRes : sortRes
  }

export default {
  components: {
    AttachmentFile,
    DefaultPanel,
    FilePond,
    ImageFab,
    SelectableRows,
  },
  props: {
    title: {
      type: String,
      default: 'Attachments',
    },
    files: {
      type: Array,
      default: () => [],
    },
    iconClasses: {
      type: Array,
      default: () => ['fa-solid', 'fa-file-alt'],
    },
    allowUpload: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    canEmbed: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isMobile = inject('isMobileUserAgent', ref(false))
    const selectedAttachmentsIndices = ref([])
    const sorting = ref({ key: 'name', order: 'asc' })
    const sortKey = computed({
      get: () => sorting.value.key,
      set: (v) => (sorting.value.key = v),
    })
    const sortOrder = computed({
      get: () => sorting.value.order,
      set: (v) => (sorting.value.order = v),
    })
    const sortFunc = computed(() =>
      ['created', 'modified'].includes(sorting.value.key)
        ? dateSort(sorting.value.key, sortOrder.value)
        : strSort('name', sortOrder.value),
    )

    const {
      getAttachmentUrl,
      loadAttachments,
      isLoading: isLoadingAttachments,
      deleteAttachments,
      deletingAttachmentsIds,
      downloadAttachment,
      deleteSuccesses,
      doAndReload,
      attachments: attachmentFiles,
    } = useAttachments()

    const isDeleting = computed(() => deletingAttachmentsIds.value.length >= 1)

    const sortedFiles = computed(() => {
      const files = [...props.files]
      files.sort((a, b) => sortFunc.value(a, b))
      return files
    })

    const { filepondEl, filepondRef, filepondUploadConfig, processFile } =
      useFilePond(getAttachmentUrl)

    const handleProcessFile = doAndReload(processFile)

    const selectedFiles = computed(() =>
      selectedAttachmentsIndices.value.map((i) => sortedFiles.value[i]),
    )
    const embeddableFiles = computed(() =>
      selectedFiles.value.filter((f) => f.isEmbeddable),
    )

    const sweepableFiles = computed(() => {
      // If props.files exist, then use it;
      // otherwise use the attachmentFiles from the useAttachments composable
      let swp = []
      if (props.files.length) swp = props.files.filter((f) => f.isSweepable)
      else swp = attachmentFiles.value.filter((f) => f.isSweepable)
      return swp
    })

    const { handleEmbedImages, embeddingImageIds } = useEditor()

    const isAddingFiles = ref(false)
    function addToFilePond(event) {
      isAddingFiles.value = true
      const images = event.target.files
      filepondEl.value.addFiles(Array.from(images)).then(async () => {
        await nextTick()
        scrollToElement(filepondEl.value.$el)
        isAddingFiles.value = false
      })
    }

    return {
      isMobile,
      filepondRef,
      filepondUploadConfig,
      isAddingFiles,
      loadAttachments,
      isLoadingAttachments,
      attachedFiles: sortedFiles,
      sortKey,
      sortOrder,
      selectedFiles,
      embeddableFiles,
      sweepableFiles,
      downloadAttachment,
      deleteAttachments,
      isDeleting,
      isDeletingFile: (id) => deletingAttachmentsIds.value.includes(id),
      deleteSuccesses,
      handleProcessFile,
      selectedAttachmentsIndices,
      handleEmbedImages,
      addToFilePond,
      isEmbedding: (id) => embeddingImageIds.value.includes(id),
    }
  },
  methods: {
    pluralize,
  },
}
</script>

<style lang="scss" scoped>
.attach-list-panel {
  .fp-dropzone {
    scroll-margin-top: 100px;
  }
  .header-right-controls {
    padding: 0 0.5rem;
    display: inline-block;
  }

  .header-right-controls:empty {
    display: none;
  }
}

.attach-list-wrapper {
  header.notification-container {
    display: flex;
    flex-direction: column;
    &:empty {
      display: none;
    }
  }
  div {
    .control-bar {
      display: flex;
      padding: 0 var(--space-3);
      margin-bottom: 1rem;
      border-bottom-style: solid;
      border-bottom-color: transparent;
      border-bottom-color: var(--bg-default);

      .select select {
        border-radius: var(--radius-sm);
      }

      .fa-is-disabled {
        opacity: 0.6;
      }

      & > div {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        height: 35px;
      }

      .control-left {
        margin-right: auto;
        & > *:not(:last-child) {
          margin-right: 0.8rem;
        }
      }
      .control-right {
        margin-left: auto;
        & > *:not(:first-child) {
          margin-left: 0.8rem;
        }
      }
    }
    .attach-list-content {
      max-height: 800px;
      overflow: auto;
    }
  }
}

.attached-file {
  &.is-loading {
    opacity: 0.7;
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--border-default);
  }
}
</style>
